import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../../utils/consts/translation";
import { isDevlopment } from "../../../utils/env";
import { Modal } from "../../atom/Modal";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../utils/consts/routes";
import { usePutResource } from "../../../services";
import { WarningAltFilled } from "@carbon/icons-react";
import { useUserContext } from "../../../utils/contexts/UserProvider";

interface OnBlurWindowNotificationProps {
  setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  blurCount: number;
  setBlurCount: React.Dispatch<React.SetStateAction<number>>;
}

const MAX_BLUR_COUNT = 6;

export const OnBlurWindowNotification = ({
  blurCount,
  setBlurCount,
}: OnBlurWindowNotificationProps) => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const { state } = useLocation();
  const participantExamId = state?.examId;
  const examId = state?.id;
  const { updateResource } = usePutResource(`/common/participant-exam/${participantExamId}`);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useUserContext();

  useEffect(() => {
    const handleBlur = () => {
      if (isDevlopment()) return;

      setBlurCount((prevCount) => {
        const newCount = prevCount + 1;

        if (newCount >= MAX_BLUR_COUNT) {
          setShowFinalModal(true);
        } else {
          setIsModalVisible(true);
        }

        return newCount;
      });
    };

    window.addEventListener("blur", handleBlur);
    return () => window.removeEventListener("blur", handleBlur);
  }, []);

  useEffect(() => {
    if (blurCount >= MAX_BLUR_COUNT && !isModalVisible) {
      setShowFinalModal(true);
    }

    if (blurCount >= MAX_BLUR_COUNT) {
      terminateRequest();
    } else if (blurCount < MAX_BLUR_COUNT && blurCount > 0 && isModalVisible) {
      updateBlurCountRequest();
    }
  }, [blurCount, isModalVisible]);

  const terminateRequest = async () => {
    await updateResource({
      id: participantExamId,
      is_completed: true,
      is_disqualified: true,
      window_blur_counter: 6,
      participant: user,
      exam: { id: examId },
    });
  };

  const updateBlurCountRequest = async () => {
    await updateResource({
      id: participantExamId,
      window_blur_counter: blurCount,
      participant: user,
      exam: { id: examId },
    });
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleTerminateExam = async () => {
    navigate(ROUTES.dashboard);
  };

  const getSubtitle = () => {
    if (blurCount < 5) {
      return t(
        "We have noticed that you have left the exam window. Please stay on the website for the entire duration of the test. Subsequent departures may result in the exam being interrupted and considered as failed.",
      );
    }
    if (blurCount === 5) {
      return t(
        "Please stay on the website for the entire duration of the test. If you leave the page again, the exam will be terminated and deemed to have failed.",
      );
    }
    return "";
  };

  return (
    <>
      <Modal
        isModalOpen={isModalVisible}
        closeModal={handleCloseModal}
        title={t("Warning!")}
        subtitle={getSubtitle()}
        firstButtonLabel={t("Leave")}
        firstButtonClick={handleTerminateExam}
        firstButtonType="danger"
        secondButtonLabel={t("Continue")}
        secondButtonClick={handleCloseModal}
        titleIcon={<WarningAltFilled size={31} className="text-yellow-500" />}
      />
      <Modal
        isModalOpen={showFinalModal}
        closeModal={handleTerminateExam}
        title={t("Exam Terminated")}
        subtitle={t(
          "You have violated the exam rules by leaving the site too many times. The exam has been terminated and deemed to have failed.",
        )}
        secondButtonLabel={t("Go to Dashboard")}
        secondButtonClick={handleTerminateExam}
      />
    </>
  );
};
